import { NavigationPosition } from '@unifyapps/defs/types/interface';
import { BlockThemeEnum } from '@unifyapps/defs/types/styleObject';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import useToggle from '@unifyapps/hooks/useToggle';
import { Drawer } from '@unifyapps/ui/components/Drawer';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import clsx from 'clsx';
import type { ComponentProps, CSSProperties } from 'react';
import { useMemo } from 'react';
import { useDrawerNavigatorOperations } from '../../../../no-code/hooks/useGlobalBlockActions/actions/controlDrawerNavigator';
import { useGlobalAppStateStore } from '../../../../no-code/stores/app/GlobalAppStateStore';
import { InterfaceModes } from '../../../../no-code/stores/InterfaceStore/types';
import { NotificationsPanelMobile } from '../../../Notifications';
import { DATA_NAV_ROOT_ATTRIBUTE } from '../../constants';
import { useGetSimplifiedNavigation } from '../../hooks/useGetSimplifiedNavigation';
import type { Navigation } from '../../types';
import { getPreviewNavItems } from '../../utils';
import { DrawerBody } from './components/DrawerBody';
import { DrawerHeader } from './components/DrawerHeader';

type BaseDrawerProps = ComponentProps<typeof Drawer>;

const getSlotProps = (isNotificationsPanelOpen: boolean, position: NavigationPosition) => {
  return {
    content: {
      style: {
        width: isNotificationsPanelOpen ? '100%' : '85%',
        height: '100%',
        position: 'absolute',
      },
      className: clsx(
        'ua-side-nav',
        { 'shadow-overflow-right': position === NavigationPosition.Start },
        { 'shadow-overflow-left': position === NavigationPosition.End },
      ),
      [DATA_NAV_ROOT_ATTRIBUTE]: 'drawer',
    },
    root: {
      style: {
        height: '100%',
        position: 'absolute',
        width: '100%',
      },
    },
    backdrop: {
      style: {
        position: 'absolute',
        pointerEvents: 'auto',
      },
    },
  } as BaseDrawerProps['slotProps'];
};

const DEFAULT_FEATURES = { disableBackdropClick: false, disableNavItemClick: false };

type Props = {
  navigation: Navigation;
  mode: InterfaceModes;
  app: {
    name: string;
    logo: {
      small: string;
      large: string;
    };
  };
  position: NavigationPosition;
  theme: BlockThemeEnum | undefined;
  pageEl?: HTMLDivElement | null;
  features?: {
    disableBackdropClick?: boolean;
    disableNavItemClick?: boolean;
  };
  disableEnforceFocus?: boolean;
};

export function DrawerNavigation({
  navigation: _navigation,
  app,
  mode,
  position,
  theme = BlockThemeEnum.INHERIT,
  pageEl: _pageEl,
  features = DEFAULT_FEATURES,
  disableEnforceFocus = false,
}: Props) {
  const [isNotificationsPanelOpen, { off: closeNotificationsPanel, on: openNotificationsPanel }] =
    useToggle(false);
  const { disableBackdropClick = false, disableNavItemClick = false } = features;
  const { hideDrawerNavigator } = useDrawerNavigatorOperations();

  const pageEl = useContainerElementContext();
  const adaptedNavigation = useGetSimplifiedNavigation(_navigation);
  const isNavigationDrawerOpen = useGlobalAppStateStore().hooks.useIsNavigationDrawerOpen();

  const drawerAnchor = position === NavigationPosition.Start ? 'left' : 'right';

  const brandNavigationItem = adaptedNavigation.primary.find(
    (item) => item.id === 'BRAND_NAVIGATION',
  );

  const otherPrimaryNavItems = useMemo(
    () => adaptedNavigation.primary.filter((item) => item.id !== 'BRAND_NAVIGATION'),
    [adaptedNavigation.primary],
  );

  const secondaryNavItems = useMemo(() => {
    return adaptedNavigation.secondary?.map((item) =>
      item.type === 'ACTION' && item.action === 'showNotifications'
        ? { ...item, onClick: openNotificationsPanel }
        : item,
    );
  }, [adaptedNavigation.secondary, openNotificationsPanel]);

  const adaptedSlotProps = useMemo(() => {
    const baseSlotProps = getSlotProps(isNotificationsPanelOpen, position);
    const contentStyles = ((baseSlotProps?.content ?? {}) as { style?: CSSProperties }).style;
    return {
      ...baseSlotProps,
      content: {
        ...baseSlotProps?.content,
        style: {
          ...contentStyles,
        },
        onClick: (e) => {
          e.stopPropagation();
        },
      },
      backdrop: {
        ...baseSlotProps?.backdrop,
      },
    } as BaseDrawerProps['slotProps'];
  }, [isNotificationsPanelOpen, position]);

  const onClose = useEventCallback((_event, reason) => {
    if (disableBackdropClick) return;
    if (reason === 'backdropClick') {
      hideDrawerNavigator();
    }
  });

  const navigation = useMemo(
    () =>
      getPreviewNavItems(
        { primary: otherPrimaryNavItems, secondary: secondaryNavItems },
        disableNavItemClick,
      ),
    [disableNavItemClick, otherPrimaryNavItems, secondaryNavItems],
  );

  return (
    <Drawer
      anchor={drawerAnchor}
      className="overflow-y-auto"
      container={_pageEl ?? pageEl}
      data-color-scheme={theme}
      // keepMounted set to true to enable transition
      // In builder mode, keepMounted is set to false since
      // the drawer was opening with Jerk effect
      disableEnforceFocus={disableEnforceFocus}
      keepMounted={mode !== InterfaceModes.BUILDER}
      onClose={onClose}
      open={isNavigationDrawerOpen}
      slotProps={adaptedSlotProps}
    >
      {!isNotificationsPanelOpen ? (
        <>
          <DrawerHeader app={app} logoNavigation={brandNavigationItem} />
          <DrawerBody navigation={navigation} />
        </>
      ) : (
        <NotificationsPanelMobile
          onClose={closeNotificationsPanel}
          scheme={theme as 'light' | 'dark' | undefined}
        />
      )}
    </Drawer>
  );
}
